// The design system's "Orders & tracking" card-row list (Aug 21, 2026 rebuild).
// Markup ported from the screen-level ORDERS LIST block in
// resources/Dental supply storefront design system/Dens N Dente Tailwind.dc.html
// :2007-2044 — there is no upstream component for this, like ProductRow it's an
// extract-from-screen port. Renders sales orders as returns-style cards: qty-pill
// product lines, the chevron-cap order-id pill (treatment E, 28px row scale)
// plus one pill per RMA raised against the order, a status Badge, and the
// 4-dot progress tracker. No Actions column (removed upstream Aug 21, 2026).
//
// Only the id pill navigates — rows are not click targets, per the reference.
// The `dnd-check` SVG is copied verbatim from upstream (a DS artifact drawn via
// the dndDraw keyframe in design-system/components/keyframes.js, not an icon).
import { useNavigate } from "react-router-dom";

import { formatCurrency, formatOrderDate } from "config/config";
import { getOrderTracking, TRACKER_STEPS } from "constants/orderTracking";

import { Badge, Icon } from "@/design-system";

// The chevron-cap id pill — this list's order-id pill, also used for the RMA
// pills here and exported for the returns list (ReturnRow), so every
// clickable SO/RMA number shares one definition. `icon` (15px blue-600
// Material Symbol) or `dot` (7px blue-700) is the leading mark.
export function IdPill({ label, icon, dot = false, title, onClick }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className="group/idp shrink-0 inline-flex items-center gap-[7px] h-[28px] pl-[9px] pr-[3px] rounded-pill bg-card border border-line cursor-pointer whitespace-nowrap font-sans text-xs font-semibold text-ink-2 [transition:border-color_var(--duration-fast),background_var(--duration-fast),color_var(--duration-fast)] hover:border-blue-300 hover:bg-soft hover:text-blue-700 active:scale-[0.97] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
    >
      {icon && <Icon name={icon} size={15} className="text-blue-600" />}
      {dot && (
        <span
          className="w-[7px] h-[7px] ml-[2px] rounded-full bg-blue-700 shrink-0"
          aria-hidden="true"
        />
      )}
      <span className="font-semibold text-ink group-hover/idp:text-blue-700">
        {label}
      </span>
      <span
        className="inline-flex items-center justify-center w-[22px] h-[22px] rounded-full bg-neutral-100 text-ink-2 shrink-0 [transition:background_var(--duration-fast),color_var(--duration-fast)] group-hover/idp:bg-[var(--blue-600)] group-hover/idp:text-white"
        aria-hidden="true"
      >
        <Icon
          name="chevron_right"
          size={14}
          className="[transition:transform_var(--duration-fast)] group-hover/idp:translate-x-[2px]"
        />
      </span>
    </button>
  );
}

const GRID_COLS =
  "grid grid-cols-[minmax(0,2fr)_92px_186px_80px_200px] gap-[var(--space-3)]";

function TrackerCheck() {
  return (
    <svg
      className="dnd-check"
      width="10"
      height="10"
      viewBox="0 0 52 52"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M11 27.5 L22 38 L41 15"
        stroke="#fff"
        strokeWidth="9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ProgressTracker({ tracking }) {
  if (tracking.cancelled) {
    return (
      <span className="text-2xs text-ink-3 pt-[5px]">
        No longer in progress
      </span>
    );
  }
  const { cur, stepNow, stepPos } = tracking;
  return (
    <div className="flex flex-col gap-[9px] pt-[3px]">
      <div className="flex items-center">
        {TRACKER_STEPS.map((label, i) => {
          const done = i < cur;
          const current = i === cur;
          const dotCls = done
            ? "bg-success-500"
            : current
              ? "bg-card border-[2px] border-blue-600 [box-sizing:border-box] animate-[v2Pulse_2.2s_ease-out_infinite]"
              : "bg-neutral-200";
          return (
            <span key={label} className="contents">
              {i > 0 && (
                <span
                  className={`flex-1 h-[2px] ${i <= cur ? "bg-success-500" : "bg-neutral-200"}`}
                />
              )}
              <span
                className={`w-[18px] h-[18px] rounded-full inline-flex items-center justify-center shrink-0 ${dotCls}`}
                title={label}
              >
                {done && <TrackerCheck />}
              </span>
            </span>
          );
        })}
      </div>
      <div className="text-2xs text-ink-3">
        <span className="font-semibold text-blue-700">{stepNow}</span> · step{" "}
        {stepPos} of 4
      </div>
    </div>
  );
}

export default function OrdersTrackingList({ orders = [], emptyMessage }) {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col gap-[14px]">
      <div
        className={`${GRID_COLS} px-[22px] text-xs font-bold uppercase tracking-[var(--tracking-wide)] text-ink-3`}
      >
        <span>Order</span>
        <span>Date</span>
        <span>Status</span>
        <span>Total</span>
        <span>Progress</span>
      </div>
      {orders.length === 0 && (
        <div className="py-[var(--space-10)] px-[22px] text-center text-ink-3 text-sm">
          {emptyMessage || "You don't have any orders yet."}
        </div>
      )}
      {orders.map((order) => {
        const tracking = getOrderTracking(order.status, order.trackingnumber);
        const lines = order.lines || [];
        const lineCount = order.lineCount ?? lines.length;
        const orderId =
          order.tranid || order.transactionnumber || String(order.id);
        return (
          <div
            key={order.id}
            className={`${GRID_COLS} items-start py-[20px] px-[22px] bg-card border border-line-subtle rounded-xl shadow-xs [transition:box-shadow_var(--duration-base)_var(--ease-out),border-color_var(--duration-fast)] hover:shadow-sm hover:border-line`}
          >
            <div className="min-w-0">
              <div className="flex flex-col gap-[8px]">
                {(lines.length > 0
                  ? lines.slice(0, 2)
                  : [
                      {
                        item: "order",
                        name: order.trandisplayname,
                        quantity: null,
                      },
                    ]
                ).map((line) => (
                  <span
                    key={line.item}
                    className="flex items-start gap-[10px] text-sm font-semibold text-ink leading-[1.4] [text-wrap:pretty]"
                  >
                    {line.quantity != null && (
                      <span className="shrink-0 inline-flex items-center justify-center min-w-[34px] h-[24px] px-[7px] rounded-pill bg-soft text-2xs font-bold text-blue-700 mt-[-2px]">
                        ×{line.quantity}
                      </span>
                    )}
                    <span className="min-w-0">{line.name}</span>
                  </span>
                ))}
              </div>
              {lineCount > 2 && (
                <div className="text-xs text-ink-3 mt-[8px] pl-[44px]">
                  +{lineCount - 2} more item{lineCount - 2 !== 1 ? "s" : ""}
                </div>
              )}
              <div className="flex items-center gap-[8px] mt-[12px] flex-wrap">
                <IdPill
                  icon="receipt_long"
                  label={orderId}
                  title="View this order"
                  onClick={() =>
                    navigate(`/profile/history/order/${order.id}`, {
                      state: { orderSummary: order },
                    })
                  }
                />
                {/* Returns raised against this order (cancelled ones
                    excluded) — `returnNumbers` comes with the order feed, no
                    extra request. */}
                {(order.returnNumbers || []).map((rma) => (
                  <IdPill
                    key={rma}
                    dot
                    label={rma}
                    title={`View return ${rma}`}
                    onClick={() => navigate(`/returns/${rma}`)}
                  />
                ))}
              </div>
            </div>
            <span className="text-sm text-ink-2 pt-[3px]">
              {formatOrderDate(order.trandate)}
            </span>
            <span className="min-w-0 whitespace-nowrap">
              <Badge color={tracking.badgeColor} dot>
                {tracking.label}
              </Badge>
            </span>
            <span className="font-bold text-sm pt-[3px]">
              {order.foreigntotal != null
                ? formatCurrency(order.foreigntotal)
                : "—"}
            </span>
            <ProgressTracker tracking={tracking} />
          </div>
        );
      })}
    </div>
  );
}
